/**
 * Per-chassis ULTIMATE moves.
 *
 * Trigger conditions:
 *   - At least 2 seconds have elapsed since race start (so the player can't
 *     spam it on the spawn-frame), AND
 *   - The player has not yet used their ultimate this race
 *
 * Each chassis has its own visual + gameplay payload. The HUD surfaces an
 * "ULTIMATE READY" pulse and the player presses **U** to fire.
 *
 *   cruiser  →  WHEEL POPOFF: car spins, four wheels rocket out N/S/E/W,
 *               killing any enemy they touch.
 *   knight   →  SPIKE BURST:  expanding metal-spike ring around the car,
 *               instakills cops in its path; lasts 1.5s.
 *   tank     →  TITAN MODE:   car triples in size for 4s, instantly
 *               destroys any enemy it touches.
 *   toon     →  HULK FIST:    a giant cartoon fist drops in front of the
 *               car, screen shakes, all enemies in 250 px radius are
 *               flung outward + stunned 6s.
 *   hotrod   →  FLAME WAKE:   for 4s, a wide cone of fire trails behind
 *               the player, scorching anything in the cone.
 *
 * All effects are non-blocking and clean themselves up via per-frame TTL.
 */
const WORLD_W = 1280;
const WORLD_H = 820;

export const ULTIMATE_LABELS = {
  cruiser: "WHEEL POPOFF",
  knight: "SPIKE BURST",
  tank: "TITAN MODE",
  toon: "HULK FIST",
  hotrod: "FLAME WAKE",
};

const ULTIMATE_DURATION_MS = {
  cruiser: 1800,
  knight: 1500,
  tank: 4000,
  toon: 1100,
  hotrod: 4000,
};

// Minimum time after the race starts before the ultimate arms. Short
// enough to be available throughout a typical encounter; long enough to
// avoid an instant-fire on the very first frame.
const ARM_DELAY_MS = 2000;

// ---------------------------------------------------------------------------
// Per-frame readiness tracker. Call once per tick.
// ---------------------------------------------------------------------------
export function updateUltimateReadiness(st) {
  const now = performance.now();
  // Stamp the arm-start the first time we run, so the gate is relative to
  // the moment GameCanvas mounts (effectively race-start).
  if (st.ultimateArmedAt == null) {
    st.ultimateArmedAt = now;
  }
  st.ultimateReady = !st.ultimateUsed
    && (now - st.ultimateArmedAt) >= ARM_DELAY_MS;
}

// ---------------------------------------------------------------------------
// Fire the ultimate. Returns true if fired, false if not eligible.
// ---------------------------------------------------------------------------
export function fireUltimate(st, kind) {
  if (!st.ultimateReady || st.ultimateUsed) return false;
  st.ultimateUsed = true;
  st.ultimateReady = false;
  const now = performance.now();
  const k = (kind || "cruiser").toLowerCase();
  st.activeUltimate = {
    kind: k,
    startedAt: now,
    endsAt: now + (ULTIMATE_DURATION_MS[k] ?? 1500),
    payload: {},
  };
  const handler = ULTIMATE_HANDLERS[k] || ULTIMATE_HANDLERS.cruiser;
  handler.fire(st, st.activeUltimate);
  // Spawn a "ULTIMATE!" splash explosion under the player so the cast is
  // unmistakable.
  if (st.explosions) {
    const p = st.player;
    st.explosions.push({
      x: p.x, y: p.y, age: 0, accentColor: "#FFCC00", wreckage: false,
      particles: makeBurst(p.x, p.y, 26, "#FFCC00"),
    });
  }
  return true;
}

// ---------------------------------------------------------------------------
// Per-frame update of an active ultimate. Call once per tick.
// ---------------------------------------------------------------------------
export function updateActiveUltimate(st) {
  const u = st.activeUltimate;
  if (!u) return;
  const handler = ULTIMATE_HANDLERS[u.kind];
  if (handler && handler.update) handler.update(st, u);
  if (performance.now() >= u.endsAt) {
    if (handler && handler.cleanup) handler.cleanup(st, u);
    st.activeUltimate = null;
  }
}

export function drawActiveUltimate(ctx, st) {
  const u = st.activeUltimate;
  if (!u) return;
  const handler = ULTIMATE_HANDLERS[u.kind];
  if (handler && handler.draw) handler.draw(ctx, st, u);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function killEnemyAt(st, enemyIdx, color) {
  if (!st.enemies[enemyIdx] || st.destroyedEnemyIdx?.has(enemyIdx)) return;
  const e = st.enemies[enemyIdx];
  st.destroyedEnemyIdx.add(enemyIdx);
  if (st.explosions) {
    st.explosions.push({
      x: e.x, y: e.y, age: 0, accentColor: color || "#FF3030", wreckage: true,
      particles: makeBurst(e.x, e.y, 16, color || "#FF3030"),
    });
  }
}

function makeBurst(x, y, count, color) {
  const out = [];
  for (let i = 0; i < count; i++) {
    const a = Math.random() * Math.PI * 2;
    const sp = 2 + Math.random() * 5;
    out.push({
      x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp,
      r: 4 + Math.random() * 4, life: 30 + Math.random() * 25,
      type: "flame",
      hue: i % 3 === 0 ? "#FFE39A" : color,
    });
  }
  return out;
}

// ===========================================================================
// CRUISER — wheels pop off and rocket outwards as projectiles.
// ===========================================================================
const cruiserHandler = {
  fire(st, u) {
    const p = st.player;
    const speed = 14;
    u.payload.wheels = [
      { x: p.x, y: p.y, vx: speed, vy: 0, life: 60, hits: new Set() },
      { x: p.x, y: p.y, vx: -speed, vy: 0, life: 60, hits: new Set() },
      { x: p.x, y: p.y, vx: 0, vy: speed, life: 60, hits: new Set() },
      { x: p.x, y: p.y, vx: 0, vy: -speed, life: 60, hits: new Set() },
    ];
    u.payload.spinAngle = 0;
  },
  update(st, u) {
    u.payload.spinAngle = (u.payload.spinAngle ?? 0) + 0.6;
    for (const w of u.payload.wheels) {
      w.x += w.vx; w.y += w.vy; w.life -= 1;
      // Kill any enemy within range
      for (let i = 0; i < st.enemies.length; i++) {
        if (st.destroyedEnemyIdx?.has(i)) continue;
        if (w.hits.has(i)) continue;
        const e = st.enemies[i];
        if (Math.hypot(e.x - w.x, e.y - w.y) < 22) {
          w.hits.add(i);
          killEnemyAt(st, i, "#FFCC00");
        }
      }
    }
    u.payload.wheels = u.payload.wheels.filter((w) => w.life > 0
      && w.x > -20 && w.x < WORLD_W + 20 && w.y > -20 && w.y < WORLD_H + 20);
  },
  draw(ctx, st, u) {
    // Player car spins like a top
    const p = st.player;
    ctx.save();
    ctx.translate(p.x, p.y);
    ctx.rotate(u.payload.spinAngle ?? 0);
    ctx.translate(-p.x, -p.y);
    // (player is drawn separately by main loop — we just rotate the world
    // around it visually via overlay sparks)
    ctx.restore();
    // Wheel projectiles — chunky black tires with chrome rims + spinning
    // motion lines.
    for (const w of u.payload.wheels) {
      ctx.save();
      ctx.translate(w.x, w.y);
      ctx.rotate(performance.now() / 30);
      ctx.fillStyle = "#0A0A0A";
      ctx.fillRect(-12, -10, 24, 20);
      ctx.fillStyle = "#9c98a4";
      ctx.fillRect(-8, -6, 16, 12);
      ctx.fillStyle = "#3a3340";
      ctx.fillRect(-3, -3, 6, 6);
      ctx.restore();
      // motion sparks behind
      ctx.strokeStyle = "rgba(255, 220, 80, 0.7)";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(w.x, w.y);
      ctx.lineTo(w.x - w.vx * 2, w.y - w.vy * 2);
      ctx.stroke();
    }
  },
};

// ===========================================================================
// KNIGHT — expanding metal-spike ring around the car.
// ===========================================================================
const knightHandler = {
  fire(_st, u) {
    u.payload.r = 10;
    u.payload.maxR = 320;
    u.payload.killed = new Set();
  },
  update(st, u) {
    const p = st.player;
    u.payload.r = Math.min(u.payload.maxR, u.payload.r + 8);
    // Hit anything inside the ring's expanding wave
    for (let i = 0; i < st.enemies.length; i++) {
      if (st.destroyedEnemyIdx?.has(i)) continue;
      if (u.payload.killed.has(i)) continue;
      const e = st.enemies[i];
      if (Math.hypot(e.x - p.x, e.y - p.y) <= u.payload.r) {
        u.payload.killed.add(i);
        killEnemyAt(st, i, "#FFCC00");
      }
    }
  },
  draw(ctx, st, u) {
    const p = st.player;
    const r = u.payload.r ?? 10;
    // Glowing inner halo
    const grad = ctx.createRadialGradient(p.x, p.y, r * 0.6, p.x, p.y, r);
    grad.addColorStop(0, "rgba(255, 204, 0, 0.0)");
    grad.addColorStop(0.85, "rgba(255, 204, 0, 0.55)");
    grad.addColorStop(1, "rgba(255, 60, 30, 0.0)");
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
    ctx.fill();
    // Spike ring
    const spikes = 24;
    ctx.fillStyle = "#cfcad4";
    ctx.strokeStyle = "#0e0c12";
    ctx.lineWidth = 2;
    for (let i = 0; i < spikes; i++) {
      const a = (i / spikes) * Math.PI * 2 + performance.now() / 700;
      const x1 = p.x + Math.cos(a) * (r - 8);
      const y1 = p.y + Math.sin(a) * (r - 8);
      const x2 = p.x + Math.cos(a) * (r + 12);
      const y2 = p.y + Math.sin(a) * (r + 12);
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.lineWidth = 4;
      ctx.strokeStyle = "#9c98a4";
      ctx.stroke();
      // Spike tip
      ctx.fillStyle = "#FFD166";
      ctx.beginPath();
      ctx.arc(x2, y2, 3, 0, Math.PI * 2);
      ctx.fill();
    }
  },
};

// ===========================================================================
// TANK — TITAN MODE: player car visually triples in size, smashes anything.
// ===========================================================================
const tankHandler = {
  fire(st, u) {
    u.payload.scale = 1;
    // Tag the player so renderMap can scale them. Simpler than baking the
    // scale into the camera.
    st.player._titanScale = 1;
    st.player._titanInvuln = u.endsAt; // can't take damage during titan mode
  },
  update(st, u) {
    const t = (performance.now() - u.startedAt) / (u.endsAt - u.startedAt);
    // Ease in/out: 1→3 over first 25%, hold, 3→1 over last 25%
    let scale;
    if (t < 0.25) scale = 1 + (t / 0.25) * 2;
    else if (t > 0.75) scale = 3 - ((t - 0.75) / 0.25) * 2;
    else scale = 3;
    u.payload.scale = scale;
    st.player._titanScale = scale;
    // Trample anything within (28 * scale) of the player.
    const reach = 28 * scale;
    const p = st.player;
    for (let i = 0; i < st.enemies.length; i++) {
      if (st.destroyedEnemyIdx?.has(i)) continue;
      const e = st.enemies[i];
      if (Math.hypot(e.x - p.x, e.y - p.y) < reach) {
        killEnemyAt(st, i, "#3070FF");
      }
    }
  },
  cleanup(st) {
    st.player._titanScale = 1;
    st.player._titanInvuln = 0;
  },
  draw(ctx, st, u) {
    const p = st.player;
    const scale = u.payload.scale ?? 1;
    // Stomp shockwave ripples whenever scale crosses an integer
    const t = performance.now() / 100;
    for (let i = 0; i < 3; i++) {
      const r = 30 * scale + i * 16 + (Math.sin(t + i) * 4);
      ctx.strokeStyle = `rgba(50, 50, 80, ${0.3 - i * 0.08})`;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
      ctx.stroke();
    }
    // Glow
    const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, 80 * scale);
    grad.addColorStop(0, "rgba(48, 112, 255, 0.4)");
    grad.addColorStop(1, "rgba(48, 112, 255, 0)");
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(p.x, p.y, 80 * scale, 0, Math.PI * 2);
    ctx.fill();
  },
};

// ===========================================================================
// TOON — HULK FIST: cartoon fist drops, screen shakes, cops fly outward.
// ===========================================================================
const toonHandler = {
  fire(st, u) {
    const p = st.player;
    // Land the fist 80 px in front of the player based on their facing.
    const fx = p.x + Math.cos(p.angle) * 70;
    const fy = p.y + Math.sin(p.angle) * 70;
    u.payload.target = { x: fx, y: fy };
    u.payload.fistY = fy - 600; // start way above the screen
    u.payload.landed = false;
    // Camera shake
    st.cameraShakeUntil = performance.now() + 800;
  },
  update(st, u) {
    if (u.payload.landed) return;
    u.payload.fistY += 60;
    if (u.payload.fistY >= u.payload.target.y) {
      u.payload.landed = true;
      u.payload.fistY = u.payload.target.y;
      // Send EVERY enemy flying outward + stun for 6s.
      for (let i = 0; i < st.enemies.length; i++) {
        if (st.destroyedEnemyIdx?.has(i)) continue;
        const e = st.enemies[i];
        const dx = e.x - u.payload.target.x;
        const dy = e.y - u.payload.target.y;
        const d = Math.hypot(dx, dy) || 1;
        if (d < 280) {
          const knock = 28;
          e.vx += (dx / d) * knock;
          e.vy += (dy / d) * knock;
          e.x += (dx / d) * 30;
          e.disabled = performance.now() + 6000;
          e.hp = e.max_hp;
        }
      }
      // Big impact sparks
      if (st.explosions) {
        st.explosions.push({
          x: u.payload.target.x, y: u.payload.target.y, age: 0,
          accentColor: "#34C759", wreckage: false,
          particles: makeBurst(u.payload.target.x, u.payload.target.y, 32, "#FFCC00"),
        });
      }
    }
  },
  draw(ctx, _st, u) {
    if (!u.payload.target) return;
    const x = u.payload.target.x;
    const y = u.payload.fistY;
    // Shadow growing under the target as fist descends
    const altitude = u.payload.target.y - y;
    const shadowR = Math.max(8, 36 - altitude * 0.05);
    ctx.fillStyle = `rgba(0, 0, 0, ${0.55 - altitude * 0.0007})`;
    ctx.beginPath();
    ctx.ellipse(u.payload.target.x, u.payload.target.y, shadowR, shadowR * 0.4, 0, 0, Math.PI * 2);
    ctx.fill();
    // Cartoon fist — chunky knuckles + outline
    ctx.save();
    ctx.translate(x, y);
    ctx.fillStyle = "#FFD7A8";
    // Forearm
    ctx.fillRect(-18, -120, 36, 70);
    // Fist
    ctx.fillRect(-30, -50, 60, 60);
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 4;
    ctx.strokeRect(-18, -120, 36, 70);
    ctx.strokeRect(-30, -50, 60, 60);
    // Knuckle ridges
    ctx.fillStyle = "#000";
    for (let i = 0; i < 3; i++) {
      ctx.fillRect(-25 + i * 20, -45, 12, 3);
    }
    // Sleeve cuff
    ctx.fillStyle = "#7B2CBF";
    ctx.fillRect(-20, -120, 40, 12);
    ctx.strokeRect(-20, -120, 40, 12);
    ctx.restore();
    if (u.payload.landed) {
      // POW! starburst + text
      const t = performance.now() / 60;
      const burstR = 60 + Math.abs(Math.sin(t)) * 12;
      ctx.save();
      ctx.translate(u.payload.target.x, u.payload.target.y - 90);
      ctx.fillStyle = "#FFCC00";
      ctx.strokeStyle = "#000";
      ctx.lineWidth = 4;
      ctx.beginPath();
      const pts = 12;
      for (let i = 0; i < pts; i++) {
        const a = (i / pts) * Math.PI * 2;
        const r = i % 2 === 0 ? burstR : burstR * 0.55;
        const px = Math.cos(a) * r;
        const py = Math.sin(a) * r;
        if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = "#000";
      ctx.font = "bold 26px 'Press Start 2P', monospace";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("POW!", 0, 0);
      ctx.restore();
    }
  },
};

// ===========================================================================
// HOTROD — FLAME WAKE: wide cone of fire trails behind for 4s, scorches
// anything in the cone.
// ===========================================================================
const hotrodHandler = {
  fire(_st, u) {
    u.payload.flames = [];
  },
  update(st, u) {
    const p = st.player;
    // Spawn flame particles behind the player every frame
    const back = -1;
    const baseAngle = p.angle + Math.PI;
    for (let i = 0; i < 4; i++) {
      const spread = (Math.random() - 0.5) * 1.0;
      const a = baseAngle + spread;
      const dist = 18 + Math.random() * 12;
      u.payload.flames.push({
        x: p.x + Math.cos(a) * dist + back * Math.cos(p.angle) * 10,
        y: p.y + Math.sin(a) * dist + back * Math.sin(p.angle) * 10,
        vx: Math.cos(a) * (3 + Math.random() * 2),
        vy: Math.sin(a) * (3 + Math.random() * 2),
        r: 8 + Math.random() * 6, life: 35 + Math.random() * 20,
        hue: i % 3 === 0 ? "#FFD166" : i % 3 === 1 ? "#FF7B00" : "#E63946",
      });
    }
    // Cone scorch — kill any enemy in the back-cone within 220 px.
    for (let i = 0; i < st.enemies.length; i++) {
      if (st.destroyedEnemyIdx?.has(i)) continue;
      const e = st.enemies[i];
      const dx = e.x - p.x;
      const dy = e.y - p.y;
      const dist = Math.hypot(dx, dy);
      if (dist > 220) continue;
      // Angle from player to cop, vs. player's BACK direction.
      const angleToCop = Math.atan2(dy, dx);
      const backAngle = p.angle + Math.PI;
      let diff = angleToCop - backAngle;
      while (diff > Math.PI) diff -= Math.PI * 2;
      while (diff < -Math.PI) diff += Math.PI * 2;
      if (Math.abs(diff) < Math.PI / 3) {
        killEnemyAt(st, i, "#FF7B00");
      }
    }
    // Age existing flames
    u.payload.flames = u.payload.flames.filter((f) => {
      f.life -= 1;
      f.x += f.vx;
      f.y += f.vy;
      f.r *= 0.97;
      return f.life > 0;
    });
  },
  draw(ctx, _st, u) {
    for (const f of u.payload.flames) {
      ctx.save();
      ctx.globalAlpha = Math.min(1, f.life / 50);
      ctx.fillStyle = f.hue;
      ctx.fillRect(f.x - f.r, f.y - f.r, f.r * 2, f.r * 2);
      ctx.fillStyle = "#FFE39A";
      ctx.fillRect(f.x - f.r / 2, f.y - f.r / 2, f.r, f.r);
      ctx.restore();
    }
  },
};

const ULTIMATE_HANDLERS = {
  cruiser: cruiserHandler,
  knight: knightHandler,
  tank: tankHandler,
  toon: toonHandler,
  hotrod: hotrodHandler,
};
